import type { IOrderDeliveryAddressComponents } from '../orders/Order';
import { CartItem, ICartItem } from './CartItem';

/**
 * A failed/aborted checkout attempt recorded on the cart. Online orders are only
 * materialized AFTER payment is authorized, so a checkout that declines or is
 * abandoned at the payment step no longer creates a (cancelled) order — the
 * funnel signal is captured here instead.
 */
export interface ICheckoutFailure {
    readonly reason: string;
    /** Machine code for the failure (e.g. PRICE_CHANGED, INVALID_TIP, PAYMENT_FAILED). */
    readonly code?: string;
    readonly at: Date;
}

/**
 * How the guest expects to receive this order.
 *
 * `dineIn` is a guest who scanned the printed QR on their own table and is
 * ordering from their phone while sitting in the dining room (Guest QR
 * Platform, piece 4). It pairs with `tableGuid` the way `delivery` pairs with
 * `deliveryAddress`: without it there is nowhere to take the food.
 *
 * Kept as its own union rather than reusing `OrderType` — the web cart has no
 * `quickService`, and `pickup` is what the kitchen calls `takeOut`. The two
 * are mapped explicitly where a cart becomes an order.
 */
export type CartOrderType = 'pickup' | 'delivery' | 'dineIn';

export interface ICart {
    readonly cartId: number;
    readonly guid: string;
    readonly customerId?: string;
    readonly restaurantId: number;
    readonly tenantId: number;
    readonly items: ICartItem[];
    readonly orderType: CartOrderType;
    /**
     * The table this cart belongs to — the guid of an entry in the
     * restaurant's own floor plan, validated server-side at checkout.
     *
     * Set only on a `dineIn` cart, but carried UNCONDITIONALLY through every
     * read, write and copy, exactly like `deliveryAddress`. Nothing in this
     * class strips a field because it disagrees with `orderType`: the incident
     * recorded on `withExpiration` below was a field being DROPPED, and its
     * fix was to carry everything.
     */
    readonly tableGuid?: string;
    readonly scheduledTime?: Date;
    readonly deliveryAddress?: string;
    /** Structured delivery address — preferred over the freeform string for
     *  courier dispatch (DoorDash Drive needs structured components). The Drive
     *  quote itself is re-derived server-side at preview/checkout, so it is NOT
     *  stored on the cart. */
    readonly deliveryAddressComponents?: IOrderDeliveryAddressComponents;
    readonly configuredAt?: Date; // Timestamp when user completed order configuration
    readonly createdAt: Date;
    readonly updatedAt: Date;
    readonly expiresAt?: Date;
    // Firestore sync fields
    readonly syncedAt?: Date;
    readonly version?: number;
    readonly isGuest?: boolean;
    /** Last failed/aborted checkout attempt (set server-side at checkout). */
    readonly lastCheckoutFailure?: ICheckoutFailure;
    /** Running count of failed checkout attempts on this cart. */
    readonly checkoutFailureCount?: number;
    /**
     * When this cart became an order (set server-side at checkout success).
     * TERMINAL: a cart converts exactly once and is never reopened. A later
     * void or refund is an ORDER-level event — the order still happened — so
     * nothing may clear this field.
     */
    readonly convertedAt?: Date;
    /** The order this cart became. Set with `convertedAt`, never alone. */
    readonly orderGuid?: string;
    /** The guest code (printed QR) this cart was opened from, if any. */
    readonly guestCodeGuid?: string;
}

export interface ICartData {
    cartId: number;
    guid: string;
    customerId?: string;
    restaurantId: number;
    tenantId: number;
    items?: CartItem[];
    orderType?: CartOrderType;
    /** See `ICart.tableGuid`. */
    tableGuid?: string;
    scheduledTime?: Date;
    deliveryAddress?: string;
    deliveryAddressComponents?: IOrderDeliveryAddressComponents;
    configuredAt?: Date;
    createdAt?: Date;
    updatedAt?: Date;
    expiresAt?: Date;
    // Firestore sync fields
    syncedAt?: Date;
    version?: number;
    isGuest?: boolean;
    lastCheckoutFailure?: ICheckoutFailure;
    checkoutFailureCount?: number;
    convertedAt?: Date;
    orderGuid?: string;
    guestCodeGuid?: string;
}

export class Cart implements ICart {
    readonly cartId: number;
    readonly guid: string;
    readonly customerId?: string;
    readonly restaurantId: number;
    readonly tenantId: number;
    readonly items: CartItem[];
    readonly orderType: CartOrderType;
    readonly tableGuid?: string;
    readonly scheduledTime?: Date;
    readonly deliveryAddress?: string;
    readonly deliveryAddressComponents?: IOrderDeliveryAddressComponents;
    readonly configuredAt?: Date;
    readonly createdAt: Date;
    readonly updatedAt: Date;
    readonly expiresAt?: Date;
    // Firestore sync fields
    readonly syncedAt?: Date;
    readonly version?: number;
    readonly isGuest?: boolean;
    readonly lastCheckoutFailure?: ICheckoutFailure;
    readonly checkoutFailureCount?: number;
    readonly convertedAt?: Date;
    readonly orderGuid?: string;
    readonly guestCodeGuid?: string;

    constructor(data: ICartData) {
        this.cartId = data.cartId;
        this.guid = data.guid;
        this.customerId = data.customerId;
        this.restaurantId = data.restaurantId;
        this.tenantId = data.tenantId;
        this.items = data.items ?? [];
        this.orderType = data.orderType ?? 'pickup';
        this.tableGuid = data.tableGuid;
        this.scheduledTime = data.scheduledTime;
        this.deliveryAddress = data.deliveryAddress;
        this.deliveryAddressComponents = data.deliveryAddressComponents;
        this.configuredAt = data.configuredAt;
        this.createdAt = data.createdAt ?? new Date();
        this.updatedAt = data.updatedAt ?? new Date();
        this.expiresAt = data.expiresAt;
        // Firestore sync fields
        this.syncedAt = data.syncedAt;
        this.version = data.version ?? 1;
        this.isGuest = data.isGuest ?? true;
        this.lastCheckoutFailure = data.lastCheckoutFailure;
        this.checkoutFailureCount = data.checkoutFailureCount;
        this.convertedAt = data.convertedAt;
        this.orderGuid = data.orderGuid;
        this.guestCodeGuid = data.guestCodeGuid;
    }

    /**
     * True once this cart has become an order. Terminal and irreversible —
     * every load/write path must refuse a converted cart so a client that still
     * holds it locally cannot resurrect it and re-check-out a paid order.
     */
    get isConverted(): boolean {
        return this.convertedAt !== undefined;
    }

    get isEmpty(): boolean {
        return this.items.length === 0;
    }

    get itemCount(): number {
        return this.items.reduce((total, item) => total + item.quantity, 0);
    }

    get uniqueItemCount(): number {
        return this.items.length;
    }

    get subtotal(): number {
        return this.items.reduce((total, item) => total + item.totalPrice, 0);
    }

    get isValid(): boolean {
        return this.items.every(item => item.isValidConfiguration);
    }

    /**
     * Check if cart is expired
     * NOTE: Client doesn't enforce expiration - this is for server TTL only.
     *
     * Guest carts: Have expiresAt set → Firestore TTL deletes after 30 days
     * Authenticated carts: No expiresAt → Never deleted by server, persist indefinitely
     *
     * Client allows all carts to persist indefinitely for better UX.
     */
    get isExpired(): boolean {
        if (!this.expiresAt) return false;
        return new Date() > this.expiresAt;
    }

    get isAnonymous(): boolean {
        // Return isGuest property instead of checking customerId
        // Anonymous auth users have a customerId but are still guests
        return this.isGuest ?? true;
    }

    /**
     * Check if cart has been configured by the user
     * A configured cart has a timestamp indicating when the user completed order configuration
     */
    get isConfigured(): boolean {
        return !!this.configuredAt;
    }

    get hasItems(): boolean {
        return !this.isEmpty;
    }

    // Cart operations
    addItem(item: CartItem): Cart {
        const newItems = [...this.items, item];
        return new Cart({
            ...this.toJSON(),
            items: newItems,
            updatedAt: new Date()
        });
    }

    removeItem(cartItemId: string): Cart {
        const newItems = this.items.filter(item => item.cartItemId !== cartItemId);
        return new Cart({
            ...this.toJSON(),
            items: newItems,
            updatedAt: new Date()
        });
    }

    updateItemQuantity(cartItemId: string, quantity: number): Cart {
        if (quantity <= 0) {
            return this.removeItem(cartItemId);
        }

        const newItems = this.items.map(item =>
            item.cartItemId === cartItemId ? item.withQuantity(quantity) : item
        );

        return new Cart({
            ...this.toJSON(),
            items: newItems,
            updatedAt: new Date()
        });
    }

    updateItemNotes(cartItemId: string, notes: string): Cart {
        const newItems = this.items.map(item =>
            item.cartItemId === cartItemId ? item.withNotes(notes) : item
        );

        return new Cart({
            ...this.toJSON(),
            items: newItems,
            updatedAt: new Date()
        });
    }

    /**
     * Replace an existing cart item with an updated one (preserves position in array)
     * Used when editing an item to change multiple properties (quantity, modifiers, notes)
     */
    replaceItem(cartItemId: string, newCartItem: CartItem): Cart {
        const newItems = this.items.map(item =>
            item.cartItemId === cartItemId ? newCartItem : item
        );

        return new Cart({
            ...this.toJSON(),
            items: newItems,
            updatedAt: new Date()
        });
    }

    clear(): Cart {
        return new Cart({
            ...this.toJSON(),
            items: [],
            updatedAt: new Date()
        });
    }

    // Find items
    findItem(cartItemId: string): CartItem | undefined {
        return this.items.find(item => item.cartItemId === cartItemId);
    }

    findItemsByMenuId(menuItemId: number): CartItem[] {
        return this.items.filter(item => item.menuItemId === menuItemId);
    }

    findItemsByMenuIdentifier(menuItemId?: number, menuItemGuid?: string): CartItem[] {
        return this.items.filter(item => {
            // Match by numeric ID if provided and item has it
            if (menuItemId !== undefined && item.menuItemId === menuItemId) {
                return true;
            }
            // Match by GUID if provided and item has it
            if (menuItemGuid && item.menuItemGuid === menuItemGuid) {
                return true;
            }
            return false;
        });
    }

    // Expiration management
    //
    // Spread the full serialized cart rather than hand-listing fields. The old
    // hand-enumeration silently DROPPED scheduledTime, deliveryAddress,
    // deliveryAddressComponents, configuredAt, lastCheckoutFailure and
    // checkoutFailureCount while still carrying orderType — so a delivery cart
    // survived as orderType:'delivery' with no address. This runs on every
    // guest add-item (extendCartExpiry), writing the truncated cart to both
    // localStorage and Firestore. Mirrors addItem/removeItem above.
    withExpiration(expiresAt: Date): Cart {
        return new Cart({
            ...this.toJSON(),
            items: this.items, // Use the CartItem[] array directly
            updatedAt: new Date(),
            expiresAt
        });
    }

    extendExpiration(additionalMinutes: number = 30): Cart {
        const newExpiresAt = new Date();
        newExpiresAt.setMinutes(newExpiresAt.getMinutes() + additionalMinutes);

        return this.withExpiration(newExpiresAt);
    }

    toJSON(): ICart {
        return {
            cartId: this.cartId,
            guid: this.guid,
            customerId: this.customerId,
            restaurantId: this.restaurantId,
            tenantId: this.tenantId,
            items: this.items.map(item => item.toJSON()),
            orderType: this.orderType,
            tableGuid: this.tableGuid,
            scheduledTime: this.scheduledTime,
            deliveryAddress: this.deliveryAddress,
            deliveryAddressComponents: this.deliveryAddressComponents,
            configuredAt: this.configuredAt,
            createdAt: this.createdAt,
            updatedAt: this.updatedAt,
            expiresAt: this.expiresAt,
            // Firestore sync fields
            syncedAt: this.syncedAt,
            version: this.version,
            isGuest: this.isGuest,
            lastCheckoutFailure: this.lastCheckoutFailure,
            checkoutFailureCount: this.checkoutFailureCount,
            convertedAt: this.convertedAt,
            orderGuid: this.orderGuid,
            guestCodeGuid: this.guestCodeGuid
        };
    }

    static fromJSON(data: Record<string, unknown>): Cart {
        if (typeof data.cartId !== 'number') {
            throw new Error("Cart.fromJSON: cartId must be a number.");
        }
        if (typeof data.guid !== 'string') {
            throw new Error("Cart.fromJSON: guid must be a string.");
        }
        if (typeof data.restaurantId !== 'number') {
            throw new Error("Cart.fromJSON: restaurantId must be a number.");
        }
        if (typeof data.tenantId !== 'number') {
            throw new Error("Cart.fromJSON: tenantId must be a number.");
        }

        const items = Array.isArray(data.items)
            ? data.items.map((item: any) => CartItem.fromJSON(item))
            : [];

        const parseDate = (value: unknown): Date | undefined => {
            if (value instanceof Date) return value;
            if (typeof value === 'string') return new Date(value);
            // Handle Firestore Timestamp objects (they have a toDate() method)
            if (value && typeof value === 'object' && 'toDate' in value && typeof (value as any).toDate === 'function') {
                return (value as any).toDate();
            }
            return undefined;
        };

        const parsedScheduledTime = parseDate(data.scheduledTime);
        const parsedDeliveryAddress = typeof data.deliveryAddress === 'string' ? data.deliveryAddress : undefined;

        // Read-side normalisation of the order type, and the ONLY normalisation
        // this class performs.
        //
        // The stored value used to be cast blind (`as 'pickup' | 'delivery'`),
        // so a document carrying anything else read back as that value and
        // every downstream comparison quietly disagreed with it. Now an
        // unrecognised value falls through to the constructor's `pickup`
        // default — the same posture `CartSchema` takes, which refuses an
        // unknown member outright.
        //
        // ⚠️ THIS NORMALISES THE TYPE, NEVER THE FIELDS THAT HANG OFF IT.
        // `tableGuid` and `deliveryAddress` are carried verbatim whatever the
        // order type says, because the recorded incident on `withExpiration`
        // below was a field being DROPPED, and stripping the one field that
        // says where the food goes is that same bug with a rule attached.
        const parsedOrderType: CartOrderType | undefined =
            data.orderType === 'pickup' ||
                data.orderType === 'delivery' ||
                data.orderType === 'dineIn'
                ? data.orderType
                : undefined;

        const cart = new Cart({
            cartId: data.cartId,
            guid: data.guid,
            customerId: typeof data.customerId === 'string' ? data.customerId : undefined,
            restaurantId: data.restaurantId,
            tenantId: data.tenantId,
            items,
            orderType: parsedOrderType,
            tableGuid: typeof data.tableGuid === 'string' ? data.tableGuid : undefined,
            scheduledTime: parsedScheduledTime,
            deliveryAddress: parsedDeliveryAddress,
            deliveryAddressComponents:
                data.deliveryAddressComponents && typeof data.deliveryAddressComponents === 'object'
                    ? (data.deliveryAddressComponents as IOrderDeliveryAddressComponents)
                    : undefined,
            configuredAt: parseDate(data.configuredAt),
            createdAt: parseDate(data.createdAt),
            updatedAt: parseDate(data.updatedAt),
            expiresAt: parseDate(data.expiresAt),
            // Conversion is terminal — always carried through, never cleared.
            convertedAt: parseDate(data.convertedAt),
            orderGuid: typeof data.orderGuid === 'string' ? data.orderGuid : undefined,
            guestCodeGuid: typeof data.guestCodeGuid === 'string' ? data.guestCodeGuid : undefined,
            // Firestore sync fields
            syncedAt: parseDate(data.syncedAt),
            version: typeof data.version === 'number' ? data.version : 1,
            isGuest: typeof data.isGuest === 'boolean' ? data.isGuest : true,
            lastCheckoutFailure:
                data.lastCheckoutFailure && typeof data.lastCheckoutFailure === 'object'
                    ? {
                        reason: String((data.lastCheckoutFailure as Record<string, unknown>).reason ?? ''),
                        code:
                            typeof (data.lastCheckoutFailure as Record<string, unknown>).code === 'string'
                                ? ((data.lastCheckoutFailure as Record<string, unknown>).code as string)
                                : undefined,
                        at:
                            parseDate((data.lastCheckoutFailure as Record<string, unknown>).at) ??
                            new Date()
                    }
                    : undefined,
            checkoutFailureCount:
                typeof data.checkoutFailureCount === 'number' ? data.checkoutFailureCount : undefined
        });

        return cart;
    }

    // NOTE: Cart now requires cartId and guid from counterService.generateCartId()
    // Use CartService methods to create new carts, which will handle ID generation
}
